import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase } from "@/lib/hooks/api/utils";
import type { Contact_MeetingsUpdateInput } from "@/src/gql/graphql";
import { Contact_MeetingsUpdateInputSchema } from "@/src/gql/validation";

export function useCreateContactMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (meetingData: {
      title: string;
      contact_id: string;
      start_time: string;
      end_time: string;
      description?: string;
      location?: string;
      meeting_type?: string;
      status?: string;
      attendees?: string[];
      notes?: string;
    }) => {
      const supabase = getSupabase();
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase
        .from("contact_meetings")
        .insert([
          {
            ...meetingData,
            user_id: user.id,
            meeting_type: meetingData.meeting_type || "call",
            status: meetingData.status || "scheduled",
          },
        ])
        .select()
        .single();

      if (error) {
        console.error("Error creating contact meeting:", error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contact-meetings"] });
      toast.success("Meeting created successfully");
    },
    onError: (error) => {
      toast.error("Failed to create meeting");
      console.error("Error creating contact meeting:", error);
    },
  });
}

export function useUpdateContactMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Contact_MeetingsUpdateInput }) => {
      const validatedData = Contact_MeetingsUpdateInputSchema.parse(updates);

      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("contact_meetings")
        .update(validatedData)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("Error updating contact meeting:", error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["contact-meetings"] });
      void queryClient.invalidateQueries({ queryKey: ["contact-meetings", data.id] });
      toast.success("Meeting updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update meeting");
      console.error("Error updating contact meeting:", error);
    },
  });
}

export function useDeleteContactMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const supabase = getSupabase();
      const { error } = await supabase.from("contact_meetings").delete().eq("id", id);

      if (error) {
        console.error("Error deleting contact meeting:", error);
        throw new Error(error.message);
      }

      return { id, deleted: true };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contact-meetings"] });
      toast.success("Meeting deleted successfully");
    },
    onError: (error) => {
      toast.error("Failed to delete meeting");
      console.error("Error deleting contact meeting:", error);
    },
  });
}
